import { MouseEvent } from 'react'
import { Tree, TreeItem, TreeItemLayout } from '@fluentui/react-components'
import { cn } from '@/lib/utils'
import { Checkbox } from '@/components/shadcn/checkbox'

// Define a generic node interface that can be used at any level
export interface GenericNode {
  id: string
  title: string
  children?: GenericNode[]
  type: string
  description: string
  contentRefs?: string[] // Optional content references for leaf nodes
}

// Generic node type to handle any level of nesting
export interface TreeNodeProps {
  node: GenericNode
  selectedItems: string[]
  onCheckChange: (contentIds: string[], checked: boolean) => void
  level: number
  maxLevel?: number
  onCheckboxClick: (e: MouseEvent) => void
}

// Helper function to get all content references from a node and its descendants
export const getAllContentRefs = (node: GenericNode): string[] => {
  // If this node has content references, add them
  const contentRefs = node.contentRefs ? [...node.contentRefs] : []

  // If this node has children, recursively get all their content references
  if (node.children && node.children.length > 0) {
    for (const child of node.children) {
      contentRefs.push(...getAllContentRefs(child))
    }
  }

  // Return deduplicated content references
  return [...new Set(contentRefs)]
}

// Recursive component to render tree nodes at any nesting level
export const TreeNode = ({
  node,
  selectedItems,
  onCheckChange,
  onCheckboxClick,
  level,
  maxLevel = 5,
}: TreeNodeProps) => {
  if (level > maxLevel) return null

  const hasChildren = node.children && node.children.length > 0
  const isWeightSemibold = level === 1 // Only apply semibold to top level

  // Get all content references for this node and its descendants
  const contentRefs = getAllContentRefs(node)

  // Check if this node should be checked based on its content references
  const isChecked = contentRefs.length > 0 && contentRefs.every((id) => selectedItems.includes(id))

  // Check if this node should be indeterminate (some but not all content refs are selected)
  const isIndeterminate =
    contentRefs.length > 0 &&
    contentRefs.some((id) => selectedItems.includes(id)) &&
    !contentRefs.every((id) => selectedItems.includes(id))

  const handleCheckboxChange = (checked: boolean) => {
    onCheckChange(contentRefs, checked)
  }

  return (
    <TreeItem key={node.id} value={node.id} itemType={hasChildren ? 'branch' : 'leaf'}>
      <TreeItemLayout className="max-w-96 rounded text-foreground hover:!bg-muted hover:!text-foreground">
        <div className="flex w-full items-start">
          <span className={cn('whitespace-normal py-1 pr-10 leading-tight', isWeightSemibold ? 'font-semibold' : '')}>
            {node.title}
          </span>
          <span className="absolute right-3 top-[0.175rem]" onClick={onCheckboxClick}>
            {contentRefs.length > 0 && (
              <Checkbox
                id={`checkbox-${node.id}`}
                checked={isChecked}
                data-state={isIndeterminate ? 'indeterminate' : isChecked ? 'checked' : 'unchecked'}
                className={
                  isIndeterminate
                    ? 'data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground'
                    : ''
                }
                onCheckedChange={handleCheckboxChange}
              />
            )}
          </span>
        </div>
      </TreeItemLayout>

      {hasChildren && (
        <Tree>
          {node.children?.map((child: GenericNode) => (
            <TreeNode
              key={child.id}
              node={child}
              selectedItems={selectedItems}
              onCheckChange={onCheckChange}
              onCheckboxClick={onCheckboxClick}
              level={level + 1}
              maxLevel={maxLevel}
            />
          ))}
        </Tree>
      )}
    </TreeItem>
  )
}
